import { countryLabel } from "./pawcita/countries";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import "@/App.css";

import Landing from "@/pawcita/Landing";
import Onboarding from "@/pawcita/Onboarding";
import Privacy from "@/pawcita/Privacy";
import DeleteAccount from "@/pawcita/DeleteAccount";
import Terms from "@/pawcita/Terms";
import PublicPawcitaId from "@/pawcita/PublicPawcitaId";
import TransactionValidation from "@/pawcita/TransactionValidation";
import BenefitValidation from "@/pawcita/BenefitValidation";
import AllyDashboard from "@/pawcita/AllyDashboard";
import AllyActivation from "@/pawcita/AllyActivation";
import { Register, Login, VerifyEmail, ForgotPassword } from "@/pawcita/Auth";
import Explore from "@/pawcita/Explore";
import DogDetail from "@/pawcita/DogDetail";
import Requests from "@/pawcita/Requests";
import Chat, { ConversationList } from "@/pawcita/Chat";
import MyDogs from "@/pawcita/MyDogs";
import Profile from "@/pawcita/Profile";
import Admin from "@/pawcita/Admin";
import Passport from "@/pawcita/Passport";
import { BottomNav, TopBar } from "@/pawcita/nav";
import Allies from "@/pawcita/Allies";
import Activities from "@/pawcita/Activities";
import MyActivities from "@/pawcita/MyActivities";
import Sos from "@/pawcita/Sos";
import VetEmergency from "@/pawcita/VetEmergency";
import ActivityChat from "@/pawcita/ActivityChat";
import { Toast } from "@/pawcita/ui";

import api, { getToken, setToken, clearToken } from "@/pawcita/api";

/* -------------------------------------------------------------------------- */
/* Screen graph for the MVP                                                   */
/* -------------------------------------------------------------------------- */

const SCREEN = {
  ONBOARDING: "onboarding",
  REGISTER: "register",
  LOGIN: "login",
  FORGOT: "forgot",
  VERIFY: "verify",
  APP: "app",
};

export default function App() {
  /* ------------------------------ Top-level view: landing | app ----------- */
  const [view, setView] = useState("landing"); // "landing" | "app"

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    document.body.classList.toggle("pc-in-app", view === "app");
    return () => document.body.classList.remove("pc-in-app");
  }, [view]);

  /* ------------------------------ Auth / screen ---------------------------- */
  const [auth, setAuth] = useState(null); // user object | null
  const [screen, setScreen] = useState(SCREEN.ONBOARDING);
  const [tab, setTab] = useState("explore");
  const [selectedDog, setSelectedDog] = useState(null);
  const [activeChatId, setActiveChatId] = useState(null);
  const [activityChats, setActivityChats] = useState([]);
  const [activeActivityChat, setActiveActivityChat] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [sosMode, setSosMode] = useState("feed");
  const [sosAlertId, setSosAlertId] = useState(null);
  const [passportDog, setPassportDog] = useState(null);
  const [pendingColita, setPendingColita] = useState(null);
  const [sendingColita, setSendingColita] = useState(false);

  const loadUnread = useCallback(() => {
    api.messages.unreadCount().then((d) => setUnreadCount(d.unread || 0)).catch(() => {});
  }, []);

  useEffect(() => {
    const navTo = (u) => {
      try {
        const url = new URL(u, window.location.origin);
        const nav = url.searchParams.get("nav");
        if (nav === "chat") setTab("chat");
        else if (nav === "requests") setTab("requests");
        else if (nav === "sos") {
          const aid = url.searchParams.get("alert_id");
          setSosAlertId(aid || null);
          setSosMode("feed");
          setSelectedDog(null);
          setTab("sos");
        }
      } catch (e) { /* noop */ }
    };
    const params = new URLSearchParams(window.location.search);
    if (params.get("sos")) {
      setSosAlertId(params.get("sos"));
      setSosMode("feed");
      setTab("sos");
      window.history.replaceState({}, "", window.location.pathname);
    }
    if (params.get("nav")) {
      navTo(window.location.href);
      window.history.replaceState({}, "", window.location.pathname);
    }
    const onMsg = (e) => { if (e.data && e.data.type === "PUSH_NAV") navTo(e.data.url); };
    navigator.serviceWorker?.addEventListener?.("message", onMsg);
    return () => navigator.serviceWorker?.removeEventListener?.("message", onMsg);
  }, []);

  useEffect(() => {
    if (screen !== "app") return;
    loadUnread();
    const id = setInterval(loadUnread, 30000);
    return () => clearInterval(id);
  }, [screen, tab, loadUnread]);
  const [requestsInitialTab, setRequestsInitialTab] = useState("received");
  const [devCode, setDevCode] = useState(null);
  const [verifyMode, setVerifyMode] = useState("mock");
  const [toast, setToast] = useState(null);
  const [booting, setBooting] = useState(true);

  /* ------------------------------ Data ------------------------------------- */
  const [myDogs, setMyDogs] = useState([]);
  const [exploreDogs, setExploreDogs] = useState([]);
  const [sentRequests, setSentRequests] = useState([]);
  const [receivedRequests, setReceivedRequests] = useState([]);
  const [chats, setChats] = useState({}); // keyed by other-dog id
  const [chatIdByDog, setChatIdByDog] = useState({}); // other-dog id -> chatId

  /* ------------------------------ Helpers ---------------------------------- */
  const showToast = useCallback((message, tone = "navy") => {
    setToast({ message, tone });
    window.setTimeout(() => setToast(null), 2600);
  }, []);

  const loadDogs = useCallback(async () => setMyDogs(await api.dogs.list()), []);
  const loadExplore = useCallback(async () => setExploreDogs(await api.explore.list()), []);
  const loadRequests = useCallback(async () => {
    const [sent, received] = await Promise.all([
      api.requests.sent(),
      api.requests.received(),
    ]);
    setSentRequests(sent);
    setReceivedRequests(received);
  }, []);
  const loadChats = useCallback(async () => {
    const list = await api.chats.list();
    const obj = {};
    const map = {};
    list.forEach((c) => {
      obj[c.dog.id] = c;
      map[c.dog.id] = c.chatId;
    });
    setChats(obj);
    setChatIdByDog(map);
  }, []);
  const loadAll = useCallback(async () => {
    await Promise.all([loadDogs(), loadExplore(), loadRequests(), loadChats()]);
  }, [loadDogs, loadExplore, loadRequests, loadChats]);

  /* ------------------------------ Session bootstrap ------------------------ */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const sessionPath =
        typeof window !== "undefined" ? window.location.pathname : "/";

      const isPublicStaticRoute =
        sessionPath === "/privacy" ||
        sessionPath === "/privacy/" ||
        sessionPath === "/delete-account" ||
        sessionPath === "/delete-account/" ||
        sessionPath === "/terms" ||
        sessionPath === "/terms/" ||
        /^\/id\/[^/]+\/?$/.test(sessionPath) ||
        /^\/(?:transaction|benefit)\/[^/]+\/?$/.test(sessionPath);

      if (isPublicStaticRoute) return;

      const token = getToken();
      if (!token) {
        setBooting(false);
        return;
      }
      try {
        const user = await api.auth.me();
        if (cancelled) return;
        setAuth(user);
        setView("app");
        if (user.email_verified === false) {
          setScreen(SCREEN.VERIFY);
        } else {
          setScreen(SCREEN.APP);
          await loadAll();
        }
      } catch {
        clearToken();
      } finally {
        if (!cancelled) setBooting(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadAll]);

  /* ------------------------------ Live refresh ----------------------------- */
  // Refetch the data relevant to the tab the user just opened, so requests and
  // messages sent by the other user show up without a manual reload.
  useEffect(() => {
    if (screen !== SCREEN.APP) return;
    if (tab === "requests") loadRequests();
    else if (tab === "chat") {
      loadChats();
      api.activities.activityChats().then(setActivityChats).catch(() => setActivityChats([]));
    }
    else if (tab === "explore") loadExplore();
    else if (tab === "dogs") loadDogs();
  }, [tab, screen, loadRequests, loadChats, loadExplore, loadDogs]);

  // Light polling while inside the app to keep requests/chats fresh.
  useEffect(() => {
    if (screen !== SCREEN.APP) return undefined;
    const id = window.setInterval(() => {
      loadRequests();
      loadChats();
      if (tab === "explore") loadExplore();
    }, 12000);
    return () => window.clearInterval(id);
  }, [screen, tab, loadRequests, loadChats, loadExplore]);

  /* ------------------------------ Auth actions ----------------------------- */
  const onRegisterSubmit = async (form) => {
    const res = await api.auth.register({
      name: form.fullName,
      email: form.email,
      password: form.password,
      city: form.city,
      country_code: form.country_code,
    });
    setToken(res.token);
    setAuth(res.user);
    if (res.user.email_verified === false) {
      setDevCode(res.dev_code || null);
      setVerifyMode(res.verification_mode || "mock");
      setScreen(SCREEN.VERIFY);
    } else {
      setScreen(SCREEN.APP);
      setTab("explore");
      await loadAll();
      showToast("¡Bienvenido a PawCita! 🐾", "mint");
    }
  };

  const onLoginSubmit = async (form) => {
    const { token, user } = await api.auth.login({
      email: form.email,
      password: form.password,
    });
    setToken(token);
    setAuth(user);
    if (user.email_verified === false) {
      setDevCode(null);
      setVerifyMode(user.verification_provider === "twilio" ? "twilio" : "mock");
      setScreen(SCREEN.VERIFY);
    } else {
      setScreen(SCREEN.APP);
      setTab("explore");
      await loadAll();
      showToast("Bienvenido de vuelta 🐶", "mint");
    }
  };

  const onVerifyEmail = async (code) => {
    const { user } = await api.auth.verifyEmail(code);
    setAuth(user);
    setDevCode(null);
    setScreen(SCREEN.APP);
    setTab("explore");
    await loadAll();
    showToast("¡Correo verificado! Bienvenido a PawCita 🐾", "mint");
  };

  const onResendVerification = async () => {
    const r = await api.auth.resendVerification();
    if (r && r.dev_code) setDevCode(r.dev_code);
    return r;
  };

  const onUpdateProfile = async (fields) => {
    const updated = await api.auth.updateProfile(fields);
    setAuth(updated);
    showToast("Perfil actualizado 🐾", "mint");
    return updated;
  };

  const onLogout = async () => {
    try {
      await api.auth.logout();
    } catch {}
    clearToken();
    setAuth(null);
    setMyDogs([]);
    setExploreDogs([]);
    setSentRequests([]);
    setReceivedRequests([]);
    setChats({});
    setChatIdByDog({});
    setActiveChatId(null);
    setSelectedDog(null);
    setPendingColita(null);
    setSendingColita(false);
    setTab("explore");
    setScreen(SCREEN.ONBOARDING);
  };

  /* ------------------------------ Explore / Requests ----------------------- */
  const performSendRequest = async (dog, message, fromDog) => {
    setSendingColita(true);

    try {
      await api.requests.send(dog.id, message, fromDog.id);
      await loadRequests();
      setSelectedDog(null);
      setRequestsInitialTab("sent");
      setTab("requests");
      showToast(
        `${fromDog.name} envió un movimiento de colita 🐾`,
        "mint"
      );
      return true;
    } catch (err) {
      showToast(
        err.message || "No pudimos enviar la solicitud",
        "navy"
      );
      return false;
    } finally {
      setSendingColita(false);
    }
  };

  const sendRequest = async (
    dog,
    message = "Hola, me interesa que nuestros perros se conozcan."
  ) => {
    if (!myDogs.length) {
      showToast(
        "Agrega tu perro antes de enviar una colita.",
        "navy"
      );
      return;
    }

    if (myDogs.length === 1) {
      await performSendRequest(dog, message, myDogs[0]);
      return;
    }

    setPendingColita({ dog, message });
  };

  const confirmSendRequest = async (fromDog) => {
    if (!pendingColita) return;

    const pending = pendingColita;

    const sent = await performSendRequest(
      pending.dog,
      pending.message,
      fromDog
    );

    if (sent) {
      setPendingColita(null);
    }
  };

  const acceptRequest = async (id) => {
    const req = receivedRequests.find((r) => r.id === id);
    try {
      await api.requests.setStatus(id, "accepted");
      await Promise.all([loadRequests(), loadChats()]);
      if (req) setActiveChatId(req.dog.id);
      setTab("chat");
      showToast("¡Colita devuelta! Abrimos el chat 💬", "mint");
    } catch (err) {
      showToast(err.message || "No pudimos aceptar la solicitud", "navy");
    }
  };

  const rejectRequest = async (id) => {
    try {
      await api.requests.setStatus(id, "rejected");
      await loadRequests();
      showToast("Seguimos olfateando otras huellitas 🐾", "navy");
    } catch (err) {
      showToast(err.message || "No pudimos actualizar la solicitud", "navy");
    }
  };

  /* ------------------------------ Chat ------------------------------------- */
  const sendChatMessage = async (dogId, text) => {
    const chatId = chatIdByDog[dogId];
    if (!chatId) return;
    // Errors (e.g. blocked external contact -> HTTP 400) propagate to the
    // caller (Chat) so they render as the inline red warning, not a toast.
    await api.chats.sendMessage(chatId, text);
    await loadChats();
  };

  const updateAppointment = async (dogId, appointment) => {
    const chatId = chatIdByDog[dogId];
    if (!chatId) return;
    // Tag the proposer so both clients can tell who proposed vs who receives.
    // Preserves an existing proposed_by (confirm/cancel spreads); legacy safe.
    const withProposer = appointment.proposed_by
      ? appointment
      : { ...appointment, proposed_by: auth?.id };
    try {
      await api.chats.setAppointment(chatId, withProposer);
      await loadChats();
      if (appointment.status === "Confirmada") showToast("¡Encuentro confirmado! 🌿", "mint");
      else if (appointment.status === "Cancelada") showToast("Cita cancelada", "navy");
      else showToast("Cita agendada ✨", "mint");
    } catch (err) {
      showToast(err.message || "No se pudo actualizar la cita", "navy");
    }
  };

  /* ------------------------------ MyDogs ----------------------------------- */
  const addMyDog = async (dog, photoFiles) => {
    try {
      const created = await api.dogs.create(dog);
      const files = Array.isArray(photoFiles) ? photoFiles : photoFiles ? [photoFiles] : [];
      let photoFailed = false;
      for (const f of files) {
        try {
          await api.dogs.uploadPhoto(created.id, f);
        } catch {
          photoFailed = true;
        }
      }
      await Promise.all([loadDogs(), loadExplore()]);
      showToast(
        photoFailed
          ? `${created.name} se guardó, pero alguna foto no se pudo subir.`
          : `${created.name} fue guardado 🐾`,
        photoFailed ? "navy" : "mint"
      );
    } catch (err) {
      showToast(err.message || "No se pudo guardar el perro", "navy");
    }
  };
  const updateMyDog = async (dog, photoFiles) => {
    try {
      await api.dogs.update(dog.id, dog);
      const files = Array.isArray(photoFiles) ? photoFiles : photoFiles ? [photoFiles] : [];
      let photoFailed = false;
      for (const f of files) {
        try {
          await api.dogs.uploadPhoto(dog.id, f);
        } catch {
          photoFailed = true;
        }
      }
      await Promise.all([loadDogs(), loadExplore()]);
      showToast(
        photoFailed
          ? `${dog.name} se actualizó, pero la foto no se pudo subir.`
          : `${dog.name} actualizado ✨`,
        photoFailed ? "navy" : "mint"
      );
    } catch (err) {
      showToast(err.message || "No se pudo actualizar el perro", "navy");
    }
  };
  const deleteMyDog = async (id) => {
    try {
      await api.dogs.remove(id);
      await Promise.all([loadDogs(), loadExplore()]);
      showToast("Huellita eliminada", "navy");
    } catch (err) {
      showToast(err.message || "No se pudo eliminar el perro", "navy");
    }
  };

  /* ------------------------------ Derived ---------------------------------- */
  const pendingReceived = useMemo(
    () => receivedRequests.filter((r) => r.status === "Recibida").length,
    [receivedRequests]
  );

  /* ------------------------------ Back navigation -------------------------- */
  const handleBack = (from) => {
    switch (from) {
      case "register":
      case "login":
        setScreen(SCREEN.ONBOARDING);
        break;
      case "dogDetail":
        setSelectedDog(null);
        break;
      case "chat":
        setActiveChatId(null);
        break;
      case "explore":
        // Home tab — nothing behind it. Keep the user inside the app (session intact).
        break;
      default:
        // Other top-level tabs (requests, dogs, profile) return to Explore home,
        // never to the pre-auth onboarding screen.
        setTab("explore");
    }
  };

  /* ------------------------------ Static routes ---------------------------- */
  const pathname = typeof window !== "undefined" ? window.location.pathname : "/";
  if (
    pathname === "/ally" ||
    pathname === "/ally/"
  ) {
    window.location.replace("/aliados");
    return null;
  }

  if (
    pathname === "/aliados/activar" ||
    pathname === "/aliados/activar/"
  ) {
    return (
      <div className="App">
        <AllyActivation />
      </div>
    );
  }

  if (
    pathname === "/aliados" ||
    pathname === "/aliados/"
  ) {
    return (
      <div className="App">
        <AllyDashboard />
      </div>
    );
  }

  const benefitMatch = pathname.match(/^\/benefit\/([^/]+)\/?$/);

  if (benefitMatch) {
    let benefitIdentifier = benefitMatch[1];

    try {
      benefitIdentifier = decodeURIComponent(benefitIdentifier);
    } catch {
      // Keep raw identifier. Backend validates whether it exists.
    }

    return (
      <div className="App">
        <BenefitValidation identifier={benefitIdentifier} />
      </div>
    );
  }

  const transactionMatch = pathname.match(/^\/transaction\/([^/]+)\/?$/);

  if (transactionMatch) {
    let transactionToken = transactionMatch[1];

    try {
      transactionToken = decodeURIComponent(transactionToken);
    } catch {
      // Keep raw token. Backend validates whether it exists.
    }

    return (
      <div className="App">
        <TransactionValidation token={transactionToken} />
      </div>
    );
  }

  const pawcitaIdMatch = pathname.match(/^\/id\/([^/]+)\/?$/);

  if (pawcitaIdMatch) {
    let publicToken = pawcitaIdMatch[1];

    try {
      publicToken = decodeURIComponent(publicToken);
    } catch {
      // Keep the raw token. The backend will validate whether it exists.
    }

    return (
      <div className="App">
        <PublicPawcitaId token={publicToken} />
      </div>
    );
  }

  if (pathname === "/privacy" || pathname === "/privacy/") {
    return (
      <div className="App">
        <Privacy />
      </div>
    );
  }
  if (pathname === "/delete-account" || pathname === "/delete-account/") {
    return (
      <div className="App">
        <DeleteAccount />
      </div>
    );
  }
  if (pathname === "/terms" || pathname === "/terms/") {
    return (
      <div className="App">
        <Terms />
      </div>
    );
  }

  // Public landing view
  if (view === "landing") {
    return (
      <div className="App">
        <Landing onEnterApp={() => setView("app")} />
        {toast && <Toast message={toast.message} tone={toast.tone} />}
      </div>
    );
  }

  const goBackToWeb = () => {
    setSelectedDog(null);
    setActiveChatId(null);
    setView("landing");
  };

  if (booting) {
    return (
      <AppFrame>
        <div className="flex min-h-screen items-center justify-center" data-testid="app-booting">
          <div className="text-center">
            <div className="mx-auto mb-3 h-10 w-10 animate-spin rounded-full border-4 border-rose-200 border-t-[#FF5C5C]" />
            <p className="text-sm font-bold text-[#0B2D4D]">Cargando PawCita…</p>
          </div>
        </div>
      </AppFrame>
    );
  }

  if (screen === SCREEN.ONBOARDING) {
    return (
      <AppFrame>
        <Onboarding
          onRegister={() => setScreen(SCREEN.REGISTER)}
          onLogin={() => setScreen(SCREEN.LOGIN)}
        />
        <BackToWebFloat onClick={goBackToWeb} />
        {toast && <Toast message={toast.message} tone={toast.tone} />}
      </AppFrame>
    );
  }
  if (screen === SCREEN.REGISTER) {
    return (
      <AppFrame>
        <Register onBack={() => handleBack("register")} onSubmit={onRegisterSubmit} />
        <BackToWebFloat onClick={goBackToWeb} />
        {toast && <Toast message={toast.message} tone={toast.tone} />}
      </AppFrame>
    );
  }
  if (screen === SCREEN.LOGIN) {
    return (
      <AppFrame>
        <Login onBack={() => handleBack("login")} onSubmit={onLoginSubmit} onForgot={() => setScreen(SCREEN.FORGOT)} />
        <BackToWebFloat onClick={goBackToWeb} />
        {toast && <Toast message={toast.message} tone={toast.tone} />}
      </AppFrame>
    );
  }
  if (screen === SCREEN.FORGOT) {
    return (
      <AppFrame>
        <ForgotPassword
          onBack={() => setScreen(SCREEN.LOGIN)}
          onRequestCode={(email) => api.auth.forgotPassword(email)}
          onReset={(email, code, pw) => api.auth.resetPassword(email, code, pw)}
        />
        <BackToWebFloat onClick={goBackToWeb} />
        {toast && <Toast message={toast.message} tone={toast.tone} />}
      </AppFrame>
    );
  }
  if (screen === SCREEN.VERIFY) {
    return (
      <AppFrame>
        <VerifyEmail
          email={auth?.email || ""}
          devCode={devCode}
          mode={verifyMode}
          onVerify={onVerifyEmail}
          onResend={onResendVerification}
          onLogout={onLogout}
        />
        {toast && <Toast message={toast.message} tone={toast.tone} />}
      </AppFrame>
    );
  }

  // Authenticated app
  const activeChat = activeChatId ? chats[activeChatId] : null;
  const currentUser = {
    name: auth?.name || "",
    city: auth?.city || "",
    countryCode: auth?.country_code || "PA",
    email: auth?.email || "",
    emailVerified: auth?.email_verified !== false,
    role: auth?.role || "user",
    stats: {
      requests: sentRequests.length + receivedRequests.length,
      meetings: Object.values(chats).filter(
        (c) => c.appointment?.status === "Confirmada"
      ).length,
    },
  };

  let screenContent = null;
  if (tab === "admin" && currentUser.role === "admin") {
    return (
      <div className="App min-h-screen bg-[#0B2D4D]" data-testid="admin-fullscreen">
        <Admin onBack={() => setTab("profile")} />
        {toast && <Toast message={toast.message} tone={toast.tone} />}
      </div>
    );
  }
  if (passportDog) {
    screenContent = (
      <Passport
        dog={passportDog}
        countryCode={currentUser.countryCode}
        onBack={() => setPassportDog(null)}
        showToast={showToast}
      />
    );
  } else if (selectedDog) {
    screenContent = (
      <DogDetail
        dog={selectedDog}
        alreadySent={sentRequests.some((r) => r.dog.id === selectedDog.id)}
        hasChat={Boolean(chats[selectedDog.id])}
        onBack={() => handleBack("dogDetail")}
        onSend={(d, m) => sendRequest(d, m)}
        onOpenChat={() => {
          setSelectedDog(null);
          setActiveChatId(selectedDog.id);
          setTab("chat");
        }}
      />
    );
  } else if (tab === "explore") {
    screenContent = (
      <Explore
        dogs={exploreDogs}
        hasDogs={myDogs.length > 0}
        onAddDog={() => setTab("dogs")}
        onOpenDog={setSelectedDog}
        onSendRequest={sendRequest}
        sentRequests={sentRequests}
        myDogName={myDogs[0]?.name}
        locationLabel={
          currentUser.city ||
          countryLabel(currentUser.countryCode)
        }
        onBack={() => handleBack("explore")}
      />
    );
  } else if (tab === "allies") {
    screenContent = <Allies />;
  } else if (tab === "vetemergency") {
    screenContent = (
      <VetEmergency
        onBack={() => setTab("profile")}
        showToast={showToast}
      />
    );
  } else if (tab === "sos") {
    screenContent = (
      <Sos
        myDogs={myDogs}
        isVerified={currentUser.emailVerified}
        initialMode={sosMode}
        initialAlertId={sosAlertId}
        onAddDog={() => setTab("dogs")}
        onBack={() => setTab("explore")}
        showToast={showToast}
      />
    );
  } else if (tab === "activities") {
    screenContent = (
      <Activities
        onOpenConversation={(dogId) => {
          setActiveChatId(dogId);
          setActiveActivityChat(null);
          setTab("chat");
        }}
      />
    );
  } else if (tab === "requests") {
    screenContent = (
      <Requests
        received={receivedRequests}
        sent={sentRequests}
        key={requestsInitialTab}
        initialTab={requestsInitialTab}
        onAccept={acceptRequest}
        onReject={rejectRequest}
        onOpenChat={(id) => {
          setActiveChatId(id);
          setTab("chat");
        }}
        onOpenDog={setSelectedDog}
        onBack={() => handleBack("requests")}
      />
    );
  } else if (tab === "chat") {
    screenContent = activeActivityChat ? (
      <ActivityChat
        activityId={activeActivityChat.activityId}
        title={activeActivityChat.title}
        isAdmin={currentUser.role === "admin"}
        onBack={() => setActiveActivityChat(null)}
      />
    ) : activeChat ? (
      <Chat
        chat={activeChat}
        onSend={sendChatMessage}
        onUpdateAppointment={updateAppointment}
        onBack={() => handleBack("chat")}
      />
    ) : (
      <ConversationList
        chats={Object.values(chats)}
        activityChats={activityChats}
        onOpen={(dogId) => setActiveChatId(dogId)}
        onOpenActivity={(ac) => setActiveActivityChat({ activityId: ac.activity_id, title: ac.activity_title })}
        onBack={() => setTab("explore")}
      />
    );
  } else if (tab === "myactivities") {
    screenContent = (
      <MyActivities
        onBack={() => setTab("profile")}
        onGoActivities={() => setTab("activities")}
      />
    );
  } else if (tab === "dogs") {
    screenContent = (
      <MyDogs
        dogs={myDogs}
        onAdd={addMyDog}
        onUpdate={updateMyDog}
        onDelete={deleteMyDog}
        onOpenPassport={(dog) => setPassportDog(dog)}
        onBack={() => handleBack("dogs")}
        defaultLocation={currentUser.city}
        countryCode={currentUser.countryCode}
      />
    );
  } else if (tab === "admin") {
    screenContent = <Admin onBack={() => setTab("profile")} />;
  } else {
    screenContent = (
      <Profile
        user={currentUser}
        dogs={myDogs}
        onBack={() => handleBack("profile")}
        onLogout={onLogout}
        onUpdateProfile={onUpdateProfile}
        onGoMyDogs={() => setTab("dogs")}
        onGoMyActivities={() => setTab("myactivities")}
        onGoActivities={() => setTab("activities")}
        onGoSos={() => {
          setSosMode("feed");
          setSelectedDog(null);
          setTab("sos");
        }}
        onGoVetEmergency={() => {
          setSelectedDog(null);
          setPassportDog(null);
          setTab("vetemergency");
        }}
        isAdmin={currentUser.role === "admin"}
        onGoAdmin={() => setTab("admin")}
      />
    );
  }

  return (
    <AppFrame>
      <div className="flex min-h-screen flex-col bg-[#FBFCFF]" data-testid="app-root">
        {!selectedDog && !passportDog && (
          <TopBar
            city={
              currentUser.city ||
              countryLabel(currentUser.countryCode)
            }
            onBackToWeb={goBackToWeb}
          />
        )}
        <main className="flex-1 pb-[calc(env(safe-area-inset-bottom)+4.5rem)]">{screenContent}</main>
        <BottomNav
          active={selectedDog || passportDog ? (passportDog ? "dogs" : "explore") : tab}
          onChange={(t) => {
            if (t === "requests") setRequestsInitialTab("received");
            setSelectedDog(null);
            setPassportDog(null);
            setActiveActivityChat(null);
            setTab(t);
          }}
          badges={{ requests: pendingReceived, chat: unreadCount }}
        />

        {pendingColita && (
          <div
            className="fixed inset-0 z-[100] flex items-end justify-center bg-black/40 p-4 sm:items-center"
            role="dialog"
            aria-modal="true"
            aria-label="Elegir perro"
            data-testid="colita-dog-picker"
            onClick={() => {
              if (!sendingColita) {
                setPendingColita(null);
              }
            }}
          >
            <div
              className="w-full max-w-md rounded-[28px] bg-white p-5 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-xl font-black text-[#0B2D4D]">
                ¿Con cuál de tus perros?
              </h2>

              <p className="mt-1 text-sm text-slate-500">
                Elige quién enviará el movimiento de colita a{" "}
                <span className="font-bold">
                  {pendingColita.dog?.name}
                </span>.
              </p>

              <div className="mt-5 space-y-3">
                {myDogs.map((myDog) => (
                  <button
                    key={myDog.id}
                    type="button"
                    disabled={sendingColita}
                    data-testid={`colita-dog-${myDog.id}`}
                    onClick={() => confirmSendRequest(myDog)}
                    className="flex w-full items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3 text-left transition hover:bg-slate-50 disabled:opacity-50"
                  >
                    <img
                      src={myDog.image || "/dog-placeholder.svg"}
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src =
                          "/dog-placeholder.svg";
                      }}
                      alt={myDog.name}
                      className="h-14 w-14 rounded-2xl object-cover"
                    />

                    <span className="min-w-0 flex-1">
                      <span className="block font-black text-[#0B2D4D]">
                        {myDog.name}
                      </span>

                      {myDog.breed ? (
                        <span className="block truncate text-xs text-slate-500">
                          {myDog.breed}
                        </span>
                      ) : null}
                    </span>

                    <span className="text-2xl text-slate-400">
                      ›
                    </span>
                  </button>
                ))}
              </div>

              <button
                type="button"
                disabled={sendingColita}
                onClick={() => setPendingColita(null)}
                className="mt-4 w-full rounded-2xl px-4 py-3 text-sm font-bold text-slate-500 disabled:opacity-50"
                data-testid="colita-dog-picker-cancel"
              >
                Cancelar
              </button>
            </div>
          </div>
        )}

        {toast && <Toast message={toast.message} tone={toast.tone} />}
      </div>
    </AppFrame>
  );
}

/* -------------------------------------------------------------------------- */
/* AppFrame — mobile-first wrapper with a soft phone shell on desktop.        */
/* -------------------------------------------------------------------------- */

function AppFrame({ children }) {
  return (
    <div className="App min-h-screen bg-gradient-to-br from-rose-50 via-[#FBFCFF] to-emerald-50 md:py-6">
      <div className="mx-auto min-h-screen w-full max-w-[480px] overflow-hidden bg-[#FBFCFF] shadow-none md:min-h-[calc(100vh-3rem)] md:max-w-3xl md:rounded-[36px] md:border md:border-slate-100 md:shadow-[0_30px_80px_-30px_rgba(11,45,77,0.25)] lg:max-w-5xl">
        {children}
      </div>
    </div>
  );
}

function BackToWebFloat({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="back-to-web-float"
      className="fixed left-1/2 top-3 z-40 inline-flex -translate-x-1/2 items-center gap-1.5 rounded-full border border-slate-200 bg-white/95 px-3 py-1.5 text-xs font-bold text-[#0B2D4D] shadow-md backdrop-blur transition hover:bg-white md:left-auto md:right-6 md:translate-x-0"
    >
      <span aria-hidden="true">←</span> Volver a la web
    </button>
  );
}
